import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  CalendarDays,
  CheckCircle2,
  Cookie,
  Database,
  Eye,
  ExternalLink,
  FileClock,
  Headphones,
  Lock,
  Mail,
  RefreshCw,
  Share2,
  ShieldCheck,
  Timer,
  User,
  UserCheck,
  Users,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import DecorLeaf from '../components/DecorLeaf';
import usePageMetadata from '../hooks/usePageMetadata';
import { HEADER_OFFSET } from '../config/navigation';
import { STORE } from '../config/store';
import { focusRing, homeShell } from '../styles/ui';

// Set explicitly, not generated. Update this whenever the policy text changes.
const LAST_UPDATED = '17 August 2026';

const linkClass =
  'text-emerald-800 underline underline-offset-4 hover:text-emerald-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-700 focus-visible:ring-offset-2 rounded';

// The navigator mirrors the sections this page actually contains — the numbered
// sections are the policy's own, and their numbering is left untouched because
// the text cross-references it ("described in section 3", "details in section
// 14"). "Introduction" is the unnumbered lead-in above section 1.
const SECTIONS = [
  { id: 'introduction', label: 'Introduction', icon: BookOpen },
  { id: 'information-we-collect', label: 'Information We Collect', icon: Database },
  { id: 'information-you-provide', label: 'Information You Provide Directly', icon: User },
  { id: 'technical-information', label: 'Automatically Processed Information', icon: Timer },
  { id: 'cookies', label: 'Cookies and Similar Technologies', icon: Cookie },
  { id: 'third-party-services', label: 'Third-Party Services', icon: ExternalLink },
  { id: 'how-information-is-used', label: 'How Information May Be Used', icon: Eye },
  { id: 'sharing', label: 'Sharing of Information', icon: Share2 },
  { id: 'retention', label: 'Data Retention', icon: FileClock },
  { id: 'security', label: 'Data Security', icon: Lock },
  { id: 'childrens-privacy', label: "Children's Privacy", icon: Users },
  { id: 'your-rights', label: 'Your Privacy Choices and Rights', icon: UserCheck },
  { id: 'external-links', label: 'External Links', icon: ExternalLink },
  { id: 'changes', label: 'Changes to This Privacy Policy', icon: RefreshCw },
  { id: 'contact-us', label: 'Contact Us', icon: Mail },
];

// Descriptions are deliberately narrower than the reference's ("industry
// standard security", "committed to your privacy always"): each one restates
// something this policy actually says, so the strip makes no claim the policy
// does not support.
const TRUST_ITEMS = [
  {
    icon: ShieldCheck,
    title: 'Your Data is Safe',
    description: 'The site is served over an encrypted (HTTPS) connection.',
  },
  {
    icon: Eye,
    title: 'We are Transparent',
    description: 'This policy sets out, in plain language, what is processed and why.',
  },
  {
    icon: User,
    title: 'You are in Control',
    description: 'Privacy questions and requests are handled as described below.',
  },
  {
    icon: Lock,
    title: 'No Profiling or Ads',
    description: 'No automated profiling, personalised advertising or marketing lists.',
  },
];

const PolicySection = ({ id, title, children }) => (
  <section id={id} aria-labelledby={`${id}-heading`} className="scroll-mt-32">
    <h2
      id={`${id}-heading`}
      className="serif text-2xl md:text-3xl text-emerald-950 mb-4"
    >
      {title}
    </h2>
    <div className="text-[15px] md:text-base text-gray-700 leading-[1.8] space-y-4">{children}</div>
  </section>
);

// Reference-style tick list. The items themselves are the policy's own wording.
const CheckList = ({ items }) => (
  <ul className="bg-emerald-50/60 border border-emerald-100 rounded-2xl p-5 sm:p-6 space-y-3">
    {items.map((item) => (
      <li key={item} className="flex items-start gap-3">
        <CheckCircle2 size={20} className="text-emerald-600 shrink-0 mt-0.5" aria-hidden="true" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const handwriting = 'serif italic text-emerald-800 leading-[1.35] select-none';

const PrivacyPolicyPage = () => {
  const [activeId, setActiveId] = useState(SECTIONS[0].id);
  const activeRef = useRef(activeId);
  activeRef.current = activeId;

  usePageMetadata({
    title: 'Privacy Policy | Fresh & Homely',
    description:
      'How Fresh & Homely handles information in connection with its website — what is collected, how it is used, who it is shared with, and how to contact us about privacy.',
    canonicalPath: '/privacy-policy',
  });

  // Scroll-spy. A single observer with a top margin equal to the fixed header
  // marks a section active once its heading passes under the navbar; no timers.
  useEffect(() => {
    // Degrade gracefully where the API is missing (older browsers, jsdom): the
    // navigator still scrolls, it just does not highlight while scrolling.
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const elements = SECTIONS.map((section) => document.getElementById(section.id)).filter(Boolean);
    if (elements.length === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      {
        // Top edge sits just below the header; bottom edge keeps only the
        // upper band of the viewport eligible, so the active item tracks what
        // the reader is actually on.
        rootMargin: `-${HEADER_OFFSET}px 0px -65% 0px`,
        threshold: 0,
      }
    );

    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, []);

  const goToSection = useCallback((event, id) => {
    event.preventDefault();
    const target = document.getElementById(id);
    if (!target) return;
    const top = target.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
    window.scrollTo({ top, behavior: 'smooth' });
    setActiveId(id);
    // Keep the URL shareable and move focus for keyboard users.
    window.history.replaceState(null, '', `#${id}`);
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  }, []);

  const navItemClass = (id) =>
    `w-full flex items-start gap-3 rounded-xl px-3.5 py-2.5 text-left text-sm transition-colors ${focusRing} ${
      activeId === id
        ? 'bg-emerald-50 text-emerald-800 font-semibold border-l-[3px] border-emerald-600 pl-[calc(0.875rem-3px)]'
        : 'text-gray-600 hover:bg-emerald-50/60 hover:text-emerald-800'
    }`;

  return (
    <>
      <Navbar />

      <main>
        {/* ── Hero ───────────────────────────────────────────────────────── */}
        <section className="relative overflow-hidden pt-24 sm:pt-28 pb-10 sm:pb-12 bg-gradient-to-b from-white via-emerald-50/70 to-white">
          <div className={`${homeShell} relative`}>
            <div className="flex flex-col lg:flex-row lg:items-center gap-10 lg:gap-8">
              <div className="w-full lg:w-[44%] shrink-0">
                <span className="inline-flex items-center text-xs font-bold tracking-[0.18em] uppercase text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-4 py-2 mb-5">
                  Your Privacy Matters
                </span>
                <h1 className="serif text-4xl sm:text-5xl xl:text-6xl text-emerald-950 leading-[1.1] mb-5">
                  Privacy Policy
                </h1>
                <p className="text-base lg:text-lg text-ink-secondary leading-relaxed max-w-xl mb-6">
                  How Fresh &amp; Homely handles information in connection with this website.
                </p>
                <p className="inline-flex items-center gap-2 text-sm font-medium text-emerald-900 bg-white border border-emerald-100 rounded-full px-4 py-2 shadow-soft">
                  <CalendarDays size={16} aria-hidden="true" />
                  Last updated: {LAST_UPDATED}
                </p>
              </div>

              {/* Decorative hero artwork: a vector shield and padlock, the
                  produce crate generated for the homepage sections, and leaves.
                  No certification marks or trust badges are shown, because the
                  project holds no such certifications. */}
              <div className="relative flex-1 min-w-0" aria-hidden="true">
                <div className="relative mx-auto max-w-2xl h-[16rem] sm:h-[19rem] lg:h-[20rem] xl:h-[22rem]">
                  <span className="absolute inset-x-8 top-6 bottom-6 rounded-[3rem] bg-gradient-to-br from-emerald-100/70 via-emerald-50/60 to-transparent blur-2xl" />

                  <img
                    src="/decor-wholesale-left.png"
                    alt=""
                    className="absolute right-[6%] bottom-0 w-[42%] max-w-[15rem] object-contain"
                    style={{
                      WebkitMaskImage:
                        'radial-gradient(ellipse at 50% 60%, #000 55%, transparent 85%)',
                      maskImage: 'radial-gradient(ellipse at 50% 60%, #000 55%, transparent 85%)',
                    }}
                  />

                  <svg
                    viewBox="0 0 200 230"
                    className="absolute left-1/2 lg:left-[38%] top-1/2 -translate-x-1/2 -translate-y-1/2 h-[85%] w-auto drop-shadow-2xl"
                    focusable="false"
                  >
                    <defs>
                      <linearGradient id="fnh-shield" x1="0" y1="0" x2="0.9" y2="1">
                        <stop offset="0%" stopColor="#69C158" />
                        <stop offset="55%" stopColor="#3FA84A" />
                        <stop offset="100%" stopColor="#256E3A" />
                      </linearGradient>
                    </defs>
                    <path
                      d="M100 6 186 38v72c0 56-38 94-86 114C52 204 14 166 14 110V38Z"
                      fill="url(#fnh-shield)"
                    />
                    <path
                      d="M100 18v188c-40-18-74-52-74-98V46Z"
                      fill="#FFFFFF"
                      opacity="0.12"
                    />
                    <rect x="72" y="104" width="56" height="48" rx="10" fill="#FFFFFF" />
                    <path
                      d="M84 104V90a16 16 0 0 1 32 0v14"
                      stroke="#FFFFFF"
                      strokeWidth="11"
                      fill="none"
                      strokeLinecap="round"
                    />
                    <circle cx="100" cy="124" r="7" fill="#2E8B47" />
                    <rect x="97" y="128" width="6" height="14" rx="3" fill="#2E8B47" />
                  </svg>

                  <DecorLeaf className="absolute left-[8%] top-[18%] w-14 text-emerald-500/80 -rotate-[24deg]" />
                  <DecorLeaf className="absolute left-[18%] bottom-[12%] w-10 text-emerald-600/70 rotate-[28deg]" />
                  <DecorLeaf className="absolute right-[2%] top-[10%] w-11 text-emerald-500/70 rotate-[14deg]" />

                  {/* Kraft trust note */}
                  <div
                    className={`absolute left-[26%] sm:left-[30%] bottom-1 bg-[#F3E3C7] border border-amber-300/70 shadow-card rounded-xl px-4 py-3 text-center text-sm sm:text-base rotate-[-5deg] ${handwriting}`}
                  >
                    Your
                    <br />
                    Trust
                    <br />
                    Our Priority
                  </div>

                  <div
                    className={`hidden xl:block absolute right-0 top-2 text-right text-base xl:text-lg rotate-[6deg] ${handwriting}`}
                    style={{
                      textShadow:
                        '0 0 10px rgba(255,255,255,0.95), 0 0 22px rgba(255,255,255,0.9)',
                    }}
                  >
                    Safe
                    <br />
                    Fresh
                    <br />
                    Always
                    <br />
                    with You
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Trust strip ────────────────────────────────────────────────── */}
        <section className="pb-10 sm:pb-12 bg-white" aria-label="Privacy commitments">
          <div className={homeShell}>
            <ul className="rounded-[2rem] border border-emerald-100 bg-gradient-to-r from-emerald-50 via-emerald-50/70 to-emerald-50 px-4 sm:px-8 py-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 xl:gap-0 xl:divide-x xl:divide-emerald-200/70">
              {TRUST_ITEMS.map(({ icon: Icon, title, description }) => (
                <li key={title} className="flex items-start gap-3.5 xl:px-6 first:xl:pl-0">
                  <span className="w-12 h-12 rounded-full bg-white text-emerald-700 flex items-center justify-center shrink-0 shadow-soft">
                    <Icon size={22} aria-hidden="true" />
                  </span>
                  <span>
                    <span className="block text-[15px] font-bold text-emerald-950 leading-tight">
                      {title}
                    </span>
                    <span className="block text-[13px] text-ink-secondary leading-snug mt-1">
                      {description}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ── Policy body ────────────────────────────────────────────────── */}
        <section className="relative pb-16 sm:pb-20 bg-white overflow-hidden">
          <div className="pointer-events-none select-none absolute inset-0" aria-hidden="true">
            <DecorLeaf className="hidden xl:block absolute right-[2%] top-[8%] w-40 text-emerald-100/70 rotate-[18deg]" />
            <DecorLeaf className="hidden xl:block absolute right-[7%] top-[22%] w-24 text-emerald-100/60 -rotate-[12deg]" />
            <DecorLeaf className="hidden xl:block absolute left-[1%] bottom-[12%] w-28 text-emerald-100/60 rotate-[32deg]" />
          </div>

          <div className={`${homeShell} relative`}>
            <div className="mx-auto max-w-[78rem] grid grid-cols-1 xl:grid-cols-[26fr_74fr] gap-8 xl:gap-12 items-start">
              {/* Section navigator */}
              <nav aria-label="On this page">
                <div className="xl:sticky xl:top-28">
                <div className="hidden xl:block bg-white border border-emerald-100 rounded-3xl shadow-card p-5">
                  <h2 className="text-base font-bold text-emerald-950 mb-3 px-1">On This Page</h2>
                  <ul className="space-y-1 max-h-[55vh] overflow-y-auto no-scrollbar pr-1">
                    {SECTIONS.map(({ id, label, icon: Icon }) => (
                      <li key={id}>
                        <a
                          href={`#${id}`}
                          onClick={(event) => goToSection(event, id)}
                          aria-current={activeId === id ? 'true' : undefined}
                          className={navItemClass(id)}
                        >
                          <Icon size={17} className="shrink-0 mt-0.5" aria-hidden="true" />
                          <span>{label}</span>
                        </a>
                      </li>
                    ))}
                  </ul>

                  {/* Support card — points at the existing Support route. No
                      privacy mailbox, DPO or hotline is invented. */}
                  <div className="mt-5 rounded-2xl bg-emerald-50 border border-emerald-100 p-5">
                    <div className="flex items-start gap-3">
                      <span className="w-11 h-11 rounded-full bg-white text-emerald-700 flex items-center justify-center shrink-0 shadow-soft">
                        <Headphones size={20} aria-hidden="true" />
                      </span>
                      <div>
                        <p className="font-bold text-emerald-950">Have Questions?</p>
                        <p className="text-[13px] text-ink-secondary leading-snug mt-1">
                          We&apos;re here to help with any privacy-related questions.
                        </p>
                      </div>
                    </div>
                    <Link
                      to="/support"
                      className={`mt-4 inline-flex items-center gap-2 h-10 px-5 rounded-full bg-white border border-emerald-200 text-emerald-800 font-semibold text-sm hover:bg-white/70 transition-colors ${focusRing}`}
                    >
                      Contact Support
                      <span aria-hidden="true">→</span>
                    </Link>
                  </div>
                </div>

                {/* Below lg the navigator becomes a horizontal rail so it never
                    occupies a column of its own. */}
                <div className="xl:hidden -mx-4 px-4">
                  <h2 className="text-sm font-bold text-emerald-950 mb-2">On This Page</h2>
                  <ul className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
                    {SECTIONS.map(({ id, label }) => (
                      <li key={id} className="shrink-0">
                        <a
                          href={`#${id}`}
                          onClick={(event) => goToSection(event, id)}
                          aria-current={activeId === id ? 'true' : undefined}
                          className={`inline-flex items-center h-9 px-4 rounded-full border text-sm whitespace-nowrap transition-colors ${focusRing} ${
                            activeId === id
                              ? 'bg-emerald-600 border-emerald-600 text-white font-semibold'
                              : 'bg-white border-emerald-100 text-emerald-900'
                          }`}
                        >
                          {label}
                        </a>
                      </li>
                    ))}
                  </ul>
                  </div>
                </div>
              </nav>

              {/* Policy content — wording unchanged from the published policy. */}
              <div className="max-w-[46rem] xl:max-w-[46rem] space-y-10 sm:space-y-12">
                <PolicySection id="introduction" title="Introduction">
                  <div className="bg-emerald-50/70 border border-emerald-100 rounded-2xl p-5 sm:p-6 flex gap-4">
                    <DecorLeaf className="w-7 shrink-0 text-emerald-600" />
                    <p>
                      This Privacy Policy explains how Fresh &amp; Homely handles information in
                      connection with this website. It describes how the website works today — it is
                      a simple informational site for our store, without accounts, online ordering or
                      tracking — and we will update it if that changes.
                    </p>
                  </div>
                </PolicySection>

                <PolicySection id="information-we-collect" title="1. Information We Collect">
                  <p>
                    This website is an informational website for our store. It does not offer account
                    registration or sign-in, online ordering or checkout, payment processing, or any
                    form or field for submitting your details. Because those features do not exist,
                    the website itself does not ask you for, or gather, information such as your name,
                    contact details or payment information.
                  </p>
                  <p>
                    This is a narrow statement about the website application. Delivering any website
                    still involves standard technical information being processed by the providers who
                    serve it, which is described in section 3.
                  </p>
                </PolicySection>

                <PolicySection id="information-you-provide" title="2. Information You Provide Directly">
                  <p>
                    Since the website has no forms, anything you share with us reaches us through
                    channels outside this website — by calling the phone number published on the site,
                    or by visiting the store in person.
                  </p>
                  <p>When you contact us that way, you may choose to tell us things such as:</p>
                  <CheckList
                    items={[
                      'your name',
                      'a phone number or other contact detail you offer',
                      'details of your enquiry',
                      'the products or quantities you are looking for',
                      'wholesale or bulk supply requirements for your business',
                    ]}
                  />
                  <p>
                    You provide this voluntarily and only if you choose to. We ask for no more than
                    what we need in order to answer you.
                  </p>
                </PolicySection>

                <PolicySection
                  id="technical-information"
                  title="3. Automatically Processed Technical Information"
                >
                  <p>
                    As with any website, showing you these pages requires our hosting and
                    content-delivery providers to process standard technical information sent by your
                    browser. This can include your IP address, browser and device information, which
                    page was requested, the date and time, and diagnostic or security information.
                  </p>
                  <p>
                    This processing is part of delivering the website and keeping it available and
                    secure. The website does not operate any analytics service, advertising tool or
                    visitor-tracking system of its own, and it maintains no database in which visitor
                    records are assembled.
                  </p>
                </PolicySection>

                <PolicySection id="cookies" title="4. Cookies and Similar Technologies">
                  <p>
                    This website does not set cookies of its own, and it does not use advertising or
                    analytics cookies. It also does not store information in your browser's local
                    storage or session storage.
                  </p>
                  <p>
                    The website does load one type of third-party resource — the fonts described in
                    section 5. Those third parties decide their own use of cookies and similar
                    technologies, under their own privacy practices.
                  </p>
                  <p>
                    If we later add functionality that requires cookies or similar technologies, we
                    will update this policy and, where required, ask for consent.
                  </p>
                </PolicySection>

                <PolicySection id="third-party-services" title="5. Third-Party Services">
                  <p>The website relies on a small number of external services:</p>
                  <ul className="list-disc pl-6 space-y-3">
                    <li>
                      <span className="font-semibold text-emerald-950">Google Fonts.</span> The
                      typefaces used on this site are loaded from Google's font servers. Your browser
                      requests those font files directly from Google, which means Google receives that
                      request, including your IP address and browser information, and handles it under
                      its own privacy practices.
                    </li>
                    <li>
                      <span className="font-semibold text-emerald-950">
                        Google Maps — as a link only.
                      </span>{' '}
                      The site offers a link that opens our store location in Google Maps. Google Maps
                      is not embedded in these pages, and no map content or Google Maps code loads
                      while you browse this website. Google is only contacted if you choose to open
                      that link, at which point you are using Google's own service under its terms and
                      privacy practices.
                    </li>
                    <li>
                      <span className="font-semibold text-emerald-950">
                        Website hosting and delivery.
                      </span>{' '}
                      The site is published as a static website on Amazon Web Services infrastructure,
                      which serves the pages to your browser and processes the technical request
                      information described in section 3.
                    </li>
                  </ul>
                  <p>
                    Each of these providers processes information according to its own privacy
                    practices, which we do not control.
                  </p>
                </PolicySection>

                <PolicySection id="how-information-is-used" title="6. How Information May Be Used">
                  <p>
                    Where you have given us information by contacting the store, we use it for the
                    purpose you got in touch about, and for reasonable follow-up on it. In practice
                    that means:
                  </p>
                  <CheckList
                    items={[
                      'responding to your enquiry',
                      'discussing product availability, pricing or a specific requirement',
                      'responding to wholesale and bulk supply enquiries',
                      'helping with questions about our location or visiting the store',
                      'general customer support',
                      'keeping the website available, reliable and secure',
                    ]}
                  />
                  <p>
                    We do not use this information for automated profiling, personalised advertising or
                    building marketing lists, and the website has no functionality that would do so.
                  </p>
                </PolicySection>

                <PolicySection id="sharing" title="7. Sharing of Information">
                  <p>
                    Operating the website means some information is necessarily handled by the service
                    providers named in section 5 — the hosting and content-delivery provider that
                    serves the pages, and the font provider your browser requests files from.
                  </p>
                  <p>
                    Beyond that, we do not share the details of your enquiries with third parties for
                    their own marketing purposes. Information may be disclosed where we are required to
                    do so by law, or where it is necessary to establish or defend legal rights or to
                    protect the safety of people or property.
                  </p>
                </PolicySection>

                <PolicySection id="retention" title="8. Data Retention">
                  <p>
                    The website has no customer database, so it does not build or keep visitor records
                    of its own.
                  </p>
                  <p>
                    Where you have contacted the store directly, we keep what you told us only for as
                    long as is reasonably necessary for that enquiry or the related business purpose,
                    and for any period we are required to keep records for legal or accounting reasons.
                  </p>
                  <p>
                    Technical information processed by our hosting and delivery providers is retained
                    by them in line with their own practices and retention settings.
                  </p>
                </PolicySection>

                <PolicySection id="security" title="9. Data Security">
                  <p>
                    The website is served to your browser over an encrypted (HTTPS) connection.
                    Because it offers no accounts, payments or forms, it does not hold customer
                    records or payment details.
                  </p>
                  <p>
                    We take reasonable care with information shared with us. That said, no method of
                    transmitting information over the internet or storing it electronically can be
                    guaranteed to be completely secure, and we cannot promise absolute security.
                  </p>
                </PolicySection>

                <PolicySection id="childrens-privacy" title="10. Children's Privacy">
                  <p>
                    This is a general business website for our store. It is not directed at children
                    and offers no accounts, forms or other features intended for them, so it does not
                    knowingly collect personal information from children through the website.
                  </p>
                  <p>
                    If you are a parent or guardian and believe a child has given us information by
                    contacting the store directly, please call us using the details in section 14 and
                    we will address it.
                  </p>
                </PolicySection>

                <PolicySection id="your-rights" title="11. Your Privacy Choices and Rights">
                  <p>
                    Depending on where you live and the laws that apply to you, you may have rights
                    concerning personal information — for example, asking what information we hold
                    about you, asking us to correct it, or asking us to delete it.
                  </p>
                  <p>
                    Because this website does not collect or store personal information, such requests
                    will normally concern information you shared with the store directly. If you have
                    a privacy question or want to make a request, please contact us using the details
                    in section 14 and we will respond as appropriate under the applicable law.
                  </p>
                </PolicySection>

                <PolicySection id="external-links" title="12. External Links">
                  <p>
                    This website links to external services, such as Google Maps for directions to the
                    store. When you follow a link like that, you leave this website and continue on a
                    service we do not operate or control.
                  </p>
                  <p>
                    We are not responsible for the content or the privacy practices of external
                    websites. We would encourage you to read their privacy policies where those
                    practices matter to you.
                  </p>
                </PolicySection>

                <PolicySection id="changes" title="13. Changes to This Privacy Policy">
                  <p>
                    We may update this policy when the website's functionality or our privacy
                    practices change — for example, if we introduce a contact form, online ordering or
                    any form of analytics in the future.
                  </p>
                  <p>
                    When we do, the revised policy will be published on this page with a new date.
                    Please check the "Last updated" date at the top to see when it last changed.
                  </p>
                </PolicySection>

                <PolicySection id="contact-us" title="14. Contact Us">
                  <p>
                    If you have questions about this Privacy Policy or about information you have
                    shared with us, please get in touch:
                  </p>
                  <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-6 space-y-3 shadow-soft">
                    <p className="font-semibold text-emerald-950">{STORE.name}</p>
                    <p>
                      <span className="font-medium text-emerald-950">Phone: </span>
                      <a href={STORE.phoneHref} className={linkClass}>
                        {STORE.phone}
                      </a>
                    </p>
                    <div>
                      <span className="font-medium text-emerald-950">Address: </span>
                      <address className="not-italic inline">{STORE.address}</address>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    Phone and visiting the store in person are the contact methods we publish for
                    Fresh &amp; Homely. If we add other channels later, we will list them here.
                  </p>
                </PolicySection>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default PrivacyPolicyPage;
